import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { NavLink } from 'react-router-dom'

import { BrandMark } from '@/components/common/BrandMark'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { hasPermission } from '@/features/auth/permissions'
import { hasModule, useEntitlements } from '@/features/entitlements/hooks'
import { navGroups } from '@/routes/navigation'

export const SIDEBAR_WIDTH = 232

/** El grupo Administración es funcional pero no es el foco del producto —
 * pesa menos visualmente (peso tipográfico e ícono más discretos) sin caer
 * por debajo de un contraste legible. */
const SECONDARY_GROUP = 'Administración'

interface SidebarProps {
  variant: 'permanent' | 'temporary'
  open: boolean
  onClose: () => void
}

export function Sidebar({ variant, open, onClose }: SidebarProps) {
  const { user } = useAuth()
  const { data: entitlements } = useEntitlements()

  // Transformación Comercial de UI: navegación agrupada por intención
  // (Inteligencia / Trabajo / Administración) en vez de una lista plana de
  // módulos — cada grupo se oculta por completo si ningún ítem es visible
  // para el rol actual. SA-8 (Sección 16): además del RBAC ya existente,
  // un ítem con `module` también se oculta si la organización no lo
  // tiene contratado — ocultar navegación no es seguridad (el backend ya
  // niega por separado, Sección 17), es solo evitar ofrecer un enlace
  // que terminaría en un 403.
  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          (!item.permission || hasPermission(user?.role, item.permission)) &&
          (!item.module || hasModule(entitlements, item.module)),
      ),
    }))
    .filter((group) => group.items.length > 0)

  const content = (
    <Stack sx={{ height: '100%' }}>
      {/* UX-9 Brand Phase B — este shell V1 sigue siendo un fallback real
          (paths.legacyDashboard: "Siempre V1, sin importar el switch —
          acceso directo de rollback/referencia", AppRoutes.tsx), no
          código muerto — recibe la misma identidad [ISOTIPO] + "GOVIA"
          que SidebarV2, sin subtítulo bajo el logo (antes: env.appName
          = "GOVIA 2.0" + "Inteligencia gubernamental"). */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 2.5 }}>
        <BrandMark size={30} />
        <Typography variant="subtitle1" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
          GOVIA
        </Typography>
      </Stack>

      <Divider />

      <List
        component="nav"
        sx={{ px: 1.5, py: 2, flex: 1, overflowY: 'auto' }}
        subheader={<li />}
      >
        {visibleGroups.map((group, groupIndex) => {
          const isSecondary = group.label === SECONDARY_GROUP
          return (
            <Box
              component="li"
              key={group.label}
              sx={{
                mb: groupIndex < visibleGroups.length - 1 ? 1.5 : 0,
                ...(isSecondary && { mt: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }),
              }}
            >
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{
                  display: 'block',
                  px: 1.5,
                  pb: 0.5,
                  letterSpacing: '0.08em',
                  fontSize: isSecondary ? '0.65rem' : undefined,
                  opacity: isSecondary ? 0.7 : 1,
                }}
              >
                {group.label}
              </Typography>
              {group.items.map((item) => {
                const Icon = item.icon
                return (
                  <ListItemButton
                    key={item.path}
                    component={NavLink}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={onClose}
                    sx={{
                      mb: 0.5,
                      '&.active': {
                        color: 'primary.light',
                        '.MuiListItemIcon-root': { color: 'primary.light' },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{ minWidth: 36, color: 'text.secondary', opacity: isSecondary ? 0.75 : 1 }}
                    >
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      slotProps={{
                        primary: {
                          variant: 'body2',
                          sx: { fontWeight: isSecondary ? 500 : 600, opacity: isSecondary ? 0.85 : 1 },
                        },
                      }}
                    />
                  </ListItemButton>
                )
              })}
            </Box>
          )
        })}
      </List>

      <Divider />
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography variant="caption" color="text.disabled">
          Centro de Inteligencia Ejecutiva
        </Typography>
      </Box>
    </Stack>
  )

  if (variant === 'permanent') {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: SIDEBAR_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        [`& .MuiDrawer-paper`]: { width: SIDEBAR_WIDTH, boxSizing: 'border-box' },
      }}
    >
      {content}
    </Drawer>
  )
}
